import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Target,
  FileText,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Globe,
  Briefcase,
  Users,
  ChevronLeft,
  ChevronRight,
  Search,
  Upload,
  X,
  ClipboardList,
  RefreshCcw,
} from 'lucide-react'
import { bookingApi, sessionsApi } from '../../lib/api/sessions'
import { profileApi } from '../../lib/api/profile'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

interface CalendlySlot {
  start: string
  start_utc: string
  end: string
  is_booked: boolean
}

interface CalendlyInterviewer {
  interviewer_id: number
  alias: string
  language: string | null
  interviewer_timezone: string | null
  slots: CalendlySlot[]
}

interface InterviewerProfile {
  id: number
  alias: string
  specializations: string | null
  tier: string | null
  hourly_rate: number
  language: string | null
  is_available: boolean
}

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export default function ClientScheduleSession() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Reschedule mode
  const rescheduleId = searchParams.get('rescheduleId')
  const isReschedule = !!rescheduleId
  const [originalBookingId, setOriginalBookingId] = useState<number | null>(null)
  const [originalInterviewerId, setOriginalInterviewerId] = useState<number | null>(null)

  // Profile form
  const [goals, setGoals] = useState('')
  const [jobDesc, setJobDesc] = useState('')
  const [targetRole, setTargetRole] = useState('')
  const [hasInterview, setHasInterview] = useState('')
  const [timezone, setTimezone] = useState('')
  const [language, setLanguage] = useState('')
  const [specialNotes, setSpecialNotes] = useState('')
  const [profileName, setProfileName] = useState('')
  const [profileEmail, setProfileEmail] = useState('')
  const [initialLoading, setInitialLoading] = useState(true)

  // Validation modal
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [showValidation, setShowValidation] = useState(false)

  // Calendar
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth())
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear())

  // Interviewers
  const [interviewerProfiles, setInterviewerProfiles] = useState<InterviewerProfile[]>([])
  const [availabilityData, setAvailabilityData] = useState<CalendlyInterviewer[]>([])
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [slotsFetched, setSlotsFetched] = useState(false)

  // Selection
  const [selectedInterviewerId, setSelectedInterviewerId] = useState<number | null>(null)
  const [selectedSlotUtc, setSelectedSlotUtc] = useState<string | null>(null)

  // Job Description PDF
  const [jobDescFile, setJobDescFile] = useState<File | null>(null)
  const [jobDescPath, setJobDescPath] = useState<string | null>(null)
  const [uploadingPdf, setUploadingPdf] = useState(false)
  const [pdfError, setPdfError] = useState('')

  // Booking
  const [bookingLoading, setBookingLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [tcAccepted, setTcAccepted] = useState(false)

  // Load profile + interviewer list on mount; pre-fill from booking if reschedule mode
  useEffect(() => {
    setInitialLoading(true)
    // Reset form state so stale values don't leak if URL rescheduleId changes
    setGoals(''); setTargetRole(''); setJobDesc(''); setJobDescPath(null)
    setSpecialNotes(''); setHasInterview(''); setSelectedInterviewerId(null)
    setSelectedSlotUtc(null); setOriginalBookingId(null); setOriginalInterviewerId(null)

    const fetches: Promise<any>[] = [
      profileApi.getProfile().catch(() => null),
      bookingApi.getInterviewers({ per_page: 50 }).catch(() => []),
    ]
    if (rescheduleId) {
      fetches.push(sessionsApi.getBooking(Number(rescheduleId)).catch(() => null))
    }
    Promise.all(fetches).then(([profileResp, ivs, booking]) => {
      // axios interceptor auto-unwraps DataResponse envelope
      if (profileResp) {
        setGoals(profileResp.interview_goals || '')
        setTargetRole(profileResp.target_role || '')
        setProfileName(profileResp.full_name || '')
        setProfileEmail(profileResp.email || '')
        setTimezone(profileResp.timezone || '')
        setLanguage(profileResp.language || '')
      }
      const list = Array.isArray(ivs) ? ivs : (ivs?.data || [])
      setInterviewerProfiles(list.filter((iv: InterviewerProfile) => iv.is_available))

      // Pre-fill from booking (reschedule mode) — booking values override profile defaults
      if (booking) {
        setOriginalBookingId(booking.booking_id)
        if (booking.job_description_text) setJobDesc(booking.job_description_text)
        if (booking.job_description_path) setJobDescPath(booking.job_description_path)
        if (booking.special_notes) setSpecialNotes(booking.special_notes)
        if (booking.target_role) setTargetRole(booking.target_role)
        if (booking.interview_goals) setGoals(booking.interview_goals)
        setHasInterview('yes')
        if (booking.interviewer_id) {
          setSelectedInterviewerId(booking.interviewer_id)
          setOriginalInterviewerId(booking.interviewer_id)
        }
      }
    }).finally(() => setInitialLoading(false))
  }, [rescheduleId])

  // ── Validation ────────────────────────────────────────────────────────────────

  const getMissingFields = (): string[] => {
    const missing: string[] = []
    if (!hasInterview) missing.push('Do you already have an interview scheduled?')
    if (!targetRole.trim()) missing.push('Target Role')
    if (!goals.trim()) missing.push('Interview Goals')
    if (!hasInterview || hasInterview === 'no') {
      if (!jobDesc.trim() && !jobDescPath) missing.push('Job Description')
    }
    if (!timezone) missing.push('Timezone (in Preferences)')
    if (!language) missing.push('Language (in Preferences)')
    return missing
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const toDateStr = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

  const fmtTime = (iso: string) => {
    const match = iso.match(/T(\d{2}):(\d{2})/)
    if (!match) return iso
    const h = parseInt(match[1])
    const m = parseInt(match[2])
    const period = h >= 12 ? 'PM' : 'AM'
    const h12 = h % 12 || 12
    return `${h12}:${m.toString().padStart(2, '0')} ${period}`
  }

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date)
    setAvailabilityData([])
    setSlotsFetched(false)
    setSelectedInterviewerId(null)
    setSelectedSlotUtc(null)
    setErrorMsg('')
  }

  const handleGetSlots = () => {
    if (!selectedDate) return

    const missing = getMissingFields()
    if (missing.length > 0) {
      setValidationErrors(missing)
      setShowValidation(true)
      return
    }

    setSlotsLoading(true)
    setAvailabilityData([])
    setSlotsFetched(false)
    setSelectedInterviewerId(null)
    setSelectedSlotUtc(null)
    setErrorMsg('')

    bookingApi.getCalendlyAvailability(toDateStr(selectedDate), timezone, language)
      .then((result) => {
        setAvailabilityData(result?.interviewers || [])
        setSlotsFetched(true)
      })
      .catch(() => {
        setErrorMsg('Failed to load slots. Please try again.')
        setSlotsFetched(true)
      })
      .finally(() => setSlotsLoading(false))
  }

  const handleSlotSelect = (interviewerId: number, slotUtc: string) => {
    setSelectedInterviewerId(interviewerId)
    setSelectedSlotUtc(slotUtc)
    setTcAccepted(false)
    setErrorMsg('')
  }

  const handlePdfSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'application/pdf') {
      setPdfError('Only PDF files are allowed.')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setPdfError('File size exceeds the 10 MB limit.')
      return
    }
    setPdfError('')
    setJobDescFile(file)
    setJobDescPath(null)
    setUploadingPdf(true)
    try {
      const result = await bookingApi.uploadJobDescriptionPdf(file)
      setJobDescPath(result.path)
      console.log('JD uploaded to S3 — path:', result.path)
      console.log('JD S3 URL:', `https://idrp-local-bucket.s3.amazonaws.com/${result.path}`)
    } catch {
      setPdfError('Upload failed. Please try again.')
      setJobDescFile(null)
    } finally {
      setUploadingPdf(false)
    }
  }

  const handlePdfRemove = () => {
    setJobDescFile(null)
    setJobDescPath(null)
    setPdfError('')
  }

  const handleBook = async () => {
    if (!selectedInterviewerId || !selectedSlotUtc) return

    const missing = getMissingFields()
    if (missing.length > 0) {
      setValidationErrors(missing)
      setShowValidation(true)
      return
    }

    setBookingLoading(true)
    setErrorMsg('')
    try {
      const result = await bookingApi.initiateBookingPayment({
        interviewer_id: selectedInterviewerId,
        start_utc: selectedSlotUtc,
        invitee_name: profileName || 'Guest',
        invitee_email: profileEmail || 'guest@example.com',
        job_description_path: jobDescPath || undefined,
        job_description_text: jobDesc || undefined,
        special_notes: specialNotes || undefined,
        target_role: targetRole || undefined,
        interview_goals: goals || undefined,
      })
      window.location.href = result.checkout_url
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Booking failed. Please try again.'
      setErrorMsg(msg)
      setBookingLoading(false)
    }
  }

  const handleReschedule = async () => {
    if (!selectedInterviewerId || !selectedSlotUtc || !originalBookingId) return

    setBookingLoading(true)
    setErrorMsg('')
    try {
      await sessionsApi.rescheduleBookingFull(originalBookingId, {
        new_slot_start_utc: selectedSlotUtc,
        new_interviewer_id: selectedInterviewerId,
        job_description_text: jobDesc || undefined,
        job_description_path: jobDescPath || undefined,
        special_notes: specialNotes || undefined,
        target_role: targetRole || undefined,
        interview_goals: goals || undefined,
      })
      navigate('/client/interviews')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Reschedule failed. Please try again.'
      setErrorMsg(msg)
      setBookingLoading(false)
    }
  }

  // ── Calendar grid ─────────────────────────────────────────────────────────────

  const getCalendarDays = () => {
    const firstDay = new Date(calendarYear, calendarMonth, 1)
    const lastDay = new Date(calendarYear, calendarMonth + 1, 0)
    const startPad = (firstDay.getDay() + 6) % 7
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const days: { day: number | null; date?: Date; disabled: boolean; empty: boolean; today?: boolean; selected?: boolean }[] = []

    for (let i = 0; i < startPad; i++) days.push({ day: null, disabled: true, empty: true })
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(calendarYear, calendarMonth, d); date.setHours(0, 0, 0, 0)
      days.push({
        day: d,
        date,
        disabled: date < today,
        empty: false,
        today: date.getTime() === today.getTime(),
        selected: selectedDate?.getTime() === date.getTime(),
      })
    }
    return days
  }

  if (initialLoading) return <LoadingSpinner message="Loading..." />

  // Build slot lookup from availability data
  const slotsById: Record<number, CalendlySlot[]> = {}
  for (const iv of availabilityData) {
    slotsById[iv.interviewer_id] = iv.slots.filter(s => !s.is_booked)
  }
  const totalOpenSlots = Object.values(slotsById).reduce((n, s) => n + s.length, 0)

  // Match quality per interviewer
  type MatchQuality = 'full' | 'partial' | 'none'
  const matchById: Record<number, MatchQuality> = {}
  if (slotsFetched && timezone && language) {
    for (const iv of availabilityData) {
      const tzMatch = iv.interviewer_timezone === timezone
      const langMatch = iv.language === language
      matchById[iv.interviewer_id] =
        tzMatch && langMatch ? 'full' : tzMatch || langMatch ? 'partial' : 'none'
    }
  }

  const availIds = new Set(availabilityData.map(iv => iv.interviewer_id))
  const orderedProfiles: InterviewerProfile[] = (() => {
    const withSlots = availabilityData
      .map(iv => interviewerProfiles.find(p => p.id === iv.interviewer_id))
      .filter((p): p is InterviewerProfile => p !== undefined)
    const withoutSlots = interviewerProfiles.filter(p => !availIds.has(p.id))
    const all = [...withSlots, ...withoutSlots]
    // In reschedule mode: always show current interviewer first
    if (originalInterviewerId) {
      const idx = all.findIndex(p => p.id === originalInterviewerId)
      if (idx > 0) { const [cur] = all.splice(idx, 1); all.unshift(cur) }
    }
    return all
  })()

  return (
    <div className="client-schedule">
      <div className="page-header">
        <h1 className="page-title">{isReschedule ? 'Reschedule Session' : 'Schedule a Session'}</h1>
        <p className="page-subtitle">
          {isReschedule
            ? 'Your previous details are pre-filled. Select a new date, slot and interviewer, then confirm.'
            : 'Fill in your details, pick a date, then get available slots'}
        </p>
      </div>

      {isReschedule && (
        <div className="sch-alert" style={{ background: '#fffbeb', borderColor: '#f59e0b', color: '#92400e' }}>
          <RefreshCcw size={18} />
          <span>Reschedule mode — no payment required. Old interviewer will receive a cancellation email if you change interviewer.</span>
        </div>
      )}

      {isReschedule && originalInterviewerId && (() => {
        const currentIv = interviewerProfiles.find(p => p.id === originalInterviewerId)
        if (!currentIv) return null
        const specs = currentIv.specializations
          ? currentIv.specializations.split(',').filter(Boolean).slice(0, 3)
          : []
        return (
          <div className="current-iv-banner">
            <div className="current-iv-label">
              <Users size={15} />
              <span>Current Interviewer</span>
            </div>
            <div className="current-iv-body">
              <div className="iv-avatar" style={{ width: 40, height: 40, fontSize: 16, flexShrink: 0 }}>
                {currentIv.alias.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div className="current-iv-name">{currentIv.alias}</div>
                <div className="iv-tags" style={{ marginTop: 4 }}>
                  {currentIv.tier && <span className="tag">{currentIv.tier}</span>}
                  <span className="tag">${currentIv.hourly_rate}/hr</span>
                  {currentIv.language && <span className="tag">{currentIv.language.toUpperCase()}</span>}
                  {specs.map(s => <span key={s} className="tag">{s.trim()}</span>)}
                </div>
              </div>
              <span className="current-iv-hint">You can keep this interviewer or select a different one below.</span>
            </div>
          </div>
        )
      })()}

      {/* Payment cancelled banner */}
      {searchParams.get('payment') === 'cancelled' && (
        <div className="sch-alert error">
          <AlertCircle size={18} />
          <span>Payment was cancelled. Your slot was released — you can book again below.</span>
        </div>
      )}

      {errorMsg && (
        <div className="sch-alert error">
          <AlertCircle size={18} />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* ── Validation Modal ───────────────────────────────────────────── */}
      {showValidation && (
        <div className="val-overlay" onClick={() => setShowValidation(false)}>
          <div className="val-modal" onClick={e => e.stopPropagation()}>
            <div className="val-modal-header">
              <AlertCircle size={22} />
              <h3>Please complete required fields</h3>
              <button className="val-close" onClick={() => setShowValidation(false)}>
                <X size={18} />
              </button>
            </div>
            <p className="val-modal-desc">
              The following fields are required before you can proceed:
            </p>
            <ul className="val-list">
              {validationErrors.map(err => (
                <li key={err}>
                  <AlertCircle size={14} />
                  {err}
                </li>
              ))}
            </ul>
            <button className="btn btn-primary val-ok-btn" onClick={() => setShowValidation(false)}>
              OK, I'll fill them in
            </button>
          </div>
        </div>
      )}

      <div className="sch-grid">

        {/* ── Interview question ─────────────────────────────────────── */}
        <div className="card full-width">
          <div className="sch-card-header">
            <Target size={20} />
            <h3>Do you already have an interview scheduled? *</h3>
          </div>
          <div className="radio-group">
            {(['yes', 'no'] as const).map((v) => (
              <label key={v} className={`radio-option${hasInterview === v ? ' selected' : ''}`}>
                <input type="radio" name="has_real_interview" value={v}
                  checked={hasInterview === v} onChange={() => setHasInterview(v)} />
                <div>
                  <strong>{v === 'yes' ? 'Yes' : 'No'}</strong>
                  <span>{v === 'yes' ? 'I have an upcoming interview and want to practice' : 'I want general interview practice for my target role'}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* ── Target role ────────────────────────────────────────────── */}
        <div className="card full-width">
          <div className="sch-card-header"><Briefcase size={20} /><h3>Target Role *</h3></div>
          <input type="text" value={targetRole} onChange={e => setTargetRole(e.target.value)}
            placeholder="e.g., Senior Software Engineer, Product Manager" className="form-input" />
        </div>

        {/* ── Goals + Job description ────────────────────────────────── */}
        <div className="card">
          <div className="sch-card-header"><Target size={20} /><h3>Interview Goals *</h3></div>
          <textarea value={goals} onChange={e => setGoals(e.target.value)}
            placeholder="e.g., Practice behavioral questions, improve system design answers..."
            rows={5} className="form-input" style={{ resize: 'vertical', minHeight: 100, fontFamily: 'inherit' }} />
        </div>

        <div className="card">
          <div className="sch-card-header">
            <FileText size={20} />
            <h3>Job Description{(!hasInterview || hasInterview === 'no') ? ' *' : ''}</h3>
          </div>
          {(!hasInterview || hasInterview === 'no') && (
            <div className="required-notice">
              <AlertCircle size={15} />
              <span>
                {hasInterview === 'no'
                  ? "Since you don't have an interview scheduled, please provide a job description so we can tailor your session."
                  : 'Please provide a job description so we can tailor your session.'}
              </span>
            </div>
          )}

          {/* PDF Upload */}
          <div className="jd-upload-row">
            {!jobDescFile && !jobDescPath ? (
              <label className="jd-upload-btn">
                <Upload size={15} />
                <span>Upload Job Description (PDF)</span>
                <input type="file" accept=".pdf,application/pdf" style={{ display: 'none' }}
                  onChange={handlePdfSelect} />
              </label>
            ) : jobDescPath && !jobDescFile ? (
              /* Previously uploaded PDF (reschedule pre-fill) */
              <div className="jd-file-pill uploaded">
                <CheckCircle size={14} />
                <span>{jobDescPath.split('/').pop()}</span>
                <button type="button" className="jd-remove-btn" onClick={handlePdfRemove}
                  title="Remove file"><X size={13} /></button>
              </div>
            ) : (
              <div className={`jd-file-pill${uploadingPdf ? ' uploading' : jobDescPath ? ' uploaded' : ''}`}>
                {uploadingPdf
                  ? <><div className="spin-sm" /><span>Uploading...</span></>
                  : jobDescPath
                    ? <><CheckCircle size={14} /><span>{jobDescFile!.name}</span></>
                    : <><span>{jobDescFile!.name}</span></>
                }
                {!uploadingPdf && (
                  <button type="button" className="jd-remove-btn" onClick={handlePdfRemove}
                    title="Remove file"><X size={13} /></button>
                )}
              </div>
            )}
            {pdfError && <span className="jd-error">{pdfError}</span>}
          </div>

          <div className="jd-divider"><span>or paste text below</span></div>

          <textarea value={jobDesc} onChange={e => setJobDesc(e.target.value)}
            placeholder="Paste the full job description here..." rows={4} className="form-input"
            style={{ resize: 'vertical', minHeight: 80, fontFamily: 'inherit' }} />

        </div>

        {/* ── Special Notes or Circumstances ─────────────────────────── */}
        <div className="card full-width">
          <div className="sch-card-header">
            <ClipboardList size={20} />
            <h3>Special Notes or Circumstances</h3>
          </div>
          <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>
            Any special requirements or scheduling preferences?
          </label>
          <textarea
            value={specialNotes}
            onChange={e => setSpecialNotes(e.target.value)}
            placeholder="e.g., I prefer a female interviewer, I need accessibility accommodations, I have a specific company in mind..."
            rows={4}
            className="form-input"
            style={{ resize: 'vertical', minHeight: 100, fontFamily: 'inherit' }}
          />
        </div>

        {/* ── Preferences + Calendar ─────────────────────────────────── */}
        <div className="full-width prefs-cal-row">

          {/* Preferences */}
          <div className="card">
            <div className="sch-card-header"><Globe size={20} /><h3>Preferences *</h3></div>
            <div className="form-group">
              <label className="form-label">Duration</label>
              <div className="duration-fixed"><Clock size={15} /><span>60 minutes</span></div>
            </div>
            <div className="form-group">
              <label className="form-label">Timezone *</label>
              <select value={timezone} onChange={e => { setTimezone(e.target.value); setErrorMsg('') }} className="form-input"
                style={selectedDate && !timezone ? { borderColor: '#dc2626' } : undefined}>
                <option value="">Select your timezone</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="Europe/London">GMT (London)</option>
                <option value="Europe/Paris">Central European (CET)</option>
                <option value="Asia/Kolkata">India (IST)</option>
                <option value="Asia/Tokyo">Japan (JST)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Language *</label>
              <select value={language} onChange={e => { setLanguage(e.target.value); setErrorMsg('') }} className="form-input"
                style={selectedDate && !language ? { borderColor: '#dc2626' } : undefined}>
                <option value="">Select language</option>
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="zh">Chinese (Mandarin)</option>
                <option value="ja">Japanese</option>
                <option value="ko">Korean</option>
                <option value="pt">Portuguese</option>
                <option value="hi">Hindi</option>
                <option value="ar">Arabic</option>
              </select>
            </div>
          </div>

          {/* Calendar */}
          <div className="card">
            <div className="sch-card-header"><Calendar size={20} /><h3>Select Date *</h3></div>
            <div className="cal-header">
              <button type="button" className="cal-nav" onClick={() => {
                if (calendarMonth === 0) { setCalendarMonth(11); setCalendarYear(y => y - 1) }
                else setCalendarMonth(m => m - 1)
              }}><ChevronLeft size={15} /></button>
              <span className="cal-month">{monthNames[calendarMonth]} {calendarYear}</span>
              <button type="button" className="cal-nav" onClick={() => {
                if (calendarMonth === 11) { setCalendarMonth(0); setCalendarYear(y => y + 1) }
                else setCalendarMonth(m => m + 1)
              }}><ChevronRight size={15} /></button>
            </div>
            <div className="cal-grid">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                <div key={d} className="cal-day-hdr">{d}</div>
              ))}
              {getCalendarDays().map((day, i) => (
                <button key={i} type="button" disabled={day.disabled || day.empty}
                  onClick={() => { if (!day.disabled && !day.empty && day.date) handleDateSelect(day.date) }}
                  className={`cal-day${day.empty ? ' empty' : ''}${day.disabled && !day.empty ? ' disabled' : ''}${day.selected ? ' selected' : ''}${day.today ? ' today' : ''}`}>
                  {day.day || ''}
                </button>
              ))}
            </div>
            {selectedDate && (
              <div className="selected-date-badge">
                <CheckCircle size={13} />
                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            )}
          </div>
        </div>

        {/* ── Get Slots button ───────────────────────────────────────── */}
        {selectedDate && !slotsFetched && (
          <div className="full-width" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <button type="button" className="btn btn-primary get-slots-btn"
              onClick={handleGetSlots} disabled={slotsLoading}>
              {slotsLoading
                ? <><div className="spin-sm" /> Loading slots...</>
                : <><Search size={17} /> Get Interviewer Slots</>}
            </button>
          </div>
        )}

        {/* ── Interviewer slot cards ───────────────────────────────────── */}
        {(slotsFetched || slotsLoading) && (
          <div className="card full-width">
            <div className="sch-card-header">
              <Users size={20} />
              <h3>
                {slotsFetched
                  ? `Available Interviewers — ${selectedDate!.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                  : 'Interviewers'}
              </h3>
              {slotsLoading && <div className="spin-sm" />}
            </div>

            {slotsLoading && (
              <div className="av-banner info">
                <div className="spin-sm" />
                <div><strong>Loading slots...</strong><span>Fetching availability from Calendly.</span></div>
              </div>
            )}
            {slotsFetched && totalOpenSlots === 0 && (
              <div className="av-banner warn">
                <AlertCircle size={16} />
                <div><strong>No open slots on this date</strong><span>Try a different date from the calendar.</span></div>
              </div>
            )}
            {slotsFetched && totalOpenSlots > 0 && (
              <div className="av-banner success">
                <CheckCircle size={16} />
                <div><strong>{totalOpenSlots} slot{totalOpenSlots !== 1 ? 's' : ''} available</strong>
                  <span>Select a time to proceed to payment.</span></div>
              </div>
            )}

            <div className="iv-grid">
              {orderedProfiles.map((profile, idx) => {
                const slots = slotsById[profile.id] || []
                const isSelected = selectedInterviewerId === profile.id
                const specs = profile.specializations
                  ? profile.specializations.split(',').filter(Boolean).slice(0, 3)
                  : []
                const match = matchById[profile.id]

                const isCurrentIv = isReschedule && profile.id === originalInterviewerId
                // Find first non-current profile index for section header logic
                const nonCurrentProfiles = orderedProfiles.filter(p => p.id !== originalInterviewerId)
                const firstRecommendedIdx = orderedProfiles.indexOf(
                  nonCurrentProfiles.find(p => matchById[p.id] === 'full' || matchById[p.id] === 'partial')!
                )
                const firstOthersIdx = orderedProfiles.indexOf(
                  nonCurrentProfiles.find(p => matchById[p.id] === 'none' || matchById[p.id] === undefined)!
                )

                const showCurrentHeader = isCurrentIv && idx === 0
                const showRecommendedHeader = slotsFetched && !isCurrentIv && idx === firstRecommendedIdx && firstRecommendedIdx !== -1
                const showOthersHeader = slotsFetched && !isCurrentIv && idx === firstOthersIdx && firstOthersIdx !== -1 && firstRecommendedIdx !== -1

                return (
                  <React.Fragment key={profile.id}>
                    {showCurrentHeader && (
                      <div className="iv-section-hdr full-width">
                        <span className="iv-section-dot current" />
                        Your Current Interviewer
                      </div>
                    )}
                    {showRecommendedHeader && (
                      <div className="iv-section-hdr full-width">
                        <span className="iv-section-dot recommended" />
                        Recommended for You
                      </div>
                    )}
                    {showOthersHeader && (
                      <div className="iv-section-hdr full-width">
                        <span className="iv-section-dot others" />
                        Other Interviewers
                      </div>
                    )}
                    <div className={`iv-card${isSelected ? ' iv-card-active' : ''}${isCurrentIv ? ' iv-card-current' : ''}`}>
                      <div className="iv-card-head">
                        <div className="iv-avatar">{profile.alias.charAt(0).toUpperCase()}</div>
                        <div style={{ flex: 1 }}>
                          <div className="iv-name">{profile.alias}</div>
                          <div className="iv-tags">
                            {profile.tier && <span className="tag">{profile.tier}</span>}
                            <span className="tag">${profile.hourly_rate}/hr</span>
                            {profile.language && <span className="tag">{profile.language.toUpperCase()}</span>}
                            {specs.map(s => <span key={s} className="tag">{s.trim()}</span>)}
                          </div>
                        </div>
                        {isCurrentIv && (
                          <span className="match-badge match-current">Current</span>
                        )}
                        {slotsFetched && !isCurrentIv && match === 'full' && (
                          <span className="match-badge match-full">Best Match</span>
                        )}
                        {slotsFetched && !isCurrentIv && match === 'partial' && (
                          <span className="match-badge match-partial">Partial Match</span>
                        )}
                      </div>

                      {slotsLoading ? (
                        <div className="slot-status"><div className="spin-sm" /> Loading...</div>
                      ) : !slotsFetched ? (
                        <div className="slot-status muted">Select a date and click "Get Interviewer Slots"</div>
                      ) : slots.length === 0 ? (
                        <div className="slot-status muted">No slots available on this date</div>
                      ) : (
                        <div className="slot-times">
                          {slots.map(slot => {
                            const active = isSelected && selectedSlotUtc === slot.start_utc
                            return (
                              <button key={slot.start_utc} type="button"
                                className={`slot-btn${active ? ' slot-btn-active' : ''}`}
                                onClick={() => handleSlotSelect(profile.id, slot.start_utc)}>
                                {fmtTime(slot.start)}
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                )
              })}
            </div>
          </div>
        )}

        {/* ── Book / Reschedule button ────────────────────────────────── */}
        {selectedInterviewerId && selectedSlotUtc && (
          <div className="full-width book-row">
            <div className="book-summary">
              <CheckCircle size={16} style={{ color: '#16a34a' }} />
              <span>
                <strong>{interviewerProfiles.find(p => p.id === selectedInterviewerId)?.alias}</strong>
                {' — '}
                {selectedDate?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                {' at '}
                {fmtTime(selectedSlotUtc)}
              </span>
            </div>
            {isReschedule ? (
              <>
                <button type="button" className="btn btn-primary" style={{ padding: '14px 36px', fontSize: 16 }}
                  onClick={handleReschedule} disabled={bookingLoading}>
                  <RefreshCcw size={18} />
                  {bookingLoading ? 'Rescheduling...' : 'Confirm Reschedule'}
                </button>
                <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                  No payment required. Your session will be rescheduled and both parties will be notified.
                </p>
              </>
            ) : (
              <>
                <label className="tc-checkbox">
                  <input
                    type="checkbox"
                    checked={tcAccepted}
                    onChange={e => setTcAccepted(e.target.checked)}
                  />
                  <span>
                    I agree to the{' '}
                    <strong>Terms &amp; Conditions</strong> and{' '}
                    <strong>Payment Policy</strong> for this session.
                  </span>
                </label>
                <button type="button" className="btn btn-primary" style={{ padding: '14px 36px', fontSize: 16 }}
                  onClick={handleBook} disabled={bookingLoading || !tcAccepted}
                  title={!tcAccepted ? 'Please accept the Terms & Conditions to continue' : undefined}>
                  <Calendar size={18} />
                  {bookingLoading ? 'Redirecting to Payment...' : 'Book & Pay'}
                </button>
                <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                  You'll be taken to Stripe to complete payment securely.
                </p>
              </>
            )}
          </div>
        )}

      </div>

      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .sch-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
  .full-width { grid-column: 1 / -1; }

  .sch-card-header {
    display: flex; align-items: center; gap: 10px;
    margin-bottom: 18px; color: var(--primary-navy);
  }
  .sch-card-header h3 { font-size: 17px; font-weight: 600; }

  .sch-alert {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 14px 18px; border-radius: 10px; margin-bottom: 20px; font-size: 14px;
  }
  .sch-alert.error {
    background: rgba(239,68,68,0.08); color: #dc2626;
    border: 1px solid rgba(239,68,68,0.2);
  }

  /* Current Interviewer banner (reschedule mode) */
  .current-iv-banner {
    background: #f0f9ff; border: 1px solid #bae6fd;
    border-radius: 12px; padding: 14px 18px; margin-bottom: 20px;
  }
  .current-iv-label {
    display: flex; align-items: center; gap: 6px;
    font-size: 12px; font-weight: 600; color: #0369a1;
    text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 10px;
  }
  .current-iv-body {
    display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
  }
  .current-iv-name {
    font-size: 15px; font-weight: 600; color: var(--primary-navy);
  }
  .current-iv-hint {
    font-size: 13px; color: #0369a1; margin-left: auto;
  }

  /* Validation Modal */
  .val-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.45);
    z-index: 1000; display: flex; align-items: center; justify-content: center;
    padding: 16px;
  }
  .val-modal {
    background: white; border-radius: 16px; padding: 28px 32px;
    max-width: 460px; width: 100%; box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  }
  .val-modal-header {
    display: flex; align-items: center; gap: 10px; margin-bottom: 14px; color: #dc2626;
  }
  .val-modal-header h3 { flex: 1; font-size: 18px; font-weight: 700; }
  .val-close {
    background: none; border: none; cursor: pointer; color: var(--text-muted);
    display: flex; align-items: center; padding: 4px;
  }
  .val-close:hover { color: var(--text-dark); }
  .val-modal-desc {
    font-size: 14px; color: var(--text-muted); margin-bottom: 16px;
  }
  .val-list {
    list-style: none; display: flex; flex-direction: column; gap: 10px; margin-bottom: 24px;
  }
  .val-list li {
    display: flex; align-items: center; gap: 10px;
    font-size: 14px; font-weight: 500; color: #dc2626;
    padding: 10px 14px; background: rgba(239,68,68,0.06);
    border-radius: 8px; border-left: 3px solid #dc2626;
  }
  .val-ok-btn { width: 100%; justify-content: center; }

  /* radio */
  .radio-group { display: flex; gap: 14px; }
  .radio-option {
    flex: 1; display: flex; align-items: flex-start; gap: 10px;
    padding: 18px; border: 2px solid var(--border); border-radius: 12px;
    cursor: pointer; transition: border-color 0.2s;
  }
  .radio-option:hover { border-color: var(--primary-navy); }
  .radio-option.selected { border-color: var(--primary-navy); background: rgba(45,55,72,0.03); }
  .radio-option input { margin-top: 3px; accent-color: var(--primary-navy); }
  .radio-option strong { display: block; margin-bottom: 3px; }
  .radio-option span { font-size: 13px; color: var(--text-muted); }

  .required-notice {
    display: flex; align-items: flex-start; gap: 8px;
    padding: 10px 14px; background: rgba(234,179,8,0.08);
    border-radius: 8px; margin-bottom: 12px; font-size: 13px; color: #854d0e;
  }

  .jd-url-hint {
    font-size: 13px; color: var(--text-muted); margin: 14px 0 0; font-weight: 500;
  }

  /* prefs + calendar side-by-side */
  .prefs-cal-row {
    display: grid; grid-template-columns: 1fr 1fr; gap: 24px;
  }

  .duration-fixed {
    display: flex; align-items: center; gap: 8px;
    padding: 11px 14px; background: var(--accent-light,#f8f9fa);
    border-radius: 8px; font-size: 14px; font-weight: 500; color: var(--primary-navy);
  }

  /* calendar */
  .cal-header {
    display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px;
  }
  .cal-month { font-size: 15px; font-weight: 600; }
  .cal-nav {
    width: 30px; height: 30px; border: 1px solid var(--border); background: white;
    border-radius: 7px; display: flex; align-items: center; justify-content: center;
    cursor: pointer;
  }
  .cal-nav:hover { background: var(--accent-light,#f8f9fa); }
  .cal-grid { display: grid; grid-template-columns: repeat(7,1fr); gap: 3px; }
  .cal-day-hdr { text-align: center; font-size: 11px; font-weight: 600; color: var(--text-muted); padding: 6px 0; }
  .cal-day {
    width: 100%; aspect-ratio: 1; border: none; background: none;
    border-radius: 7px; font-size: 13px; cursor: pointer; color: var(--text-dark);
    transition: background 0.15s;
  }
  .cal-day:hover:not(.disabled):not(.empty):not(.selected) { background: var(--accent-light,#f8f9fa); }
  .cal-day.empty { cursor: default; }
  .cal-day.disabled { color: #d1d5db; cursor: not-allowed; }
  .cal-day.today { font-weight: 700; border: 2px solid var(--primary-navy); }
  .cal-day.selected { background: var(--primary-navy); color: white; font-weight: 600; }
  .selected-date-badge {
    display: flex; align-items: center; gap: 7px;
    margin-top: 12px; padding: 9px 13px;
    background: rgba(34,197,94,0.08); border-radius: 8px;
    font-size: 13px; color: #16a34a; font-weight: 500;
  }

  /* get slots button */
  .get-slots-btn { padding: 13px 36px; font-size: 15px; gap: 8px; }

  /* availability banner */
  .av-banner {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 14px 18px; border-radius: 10px; margin-bottom: 18px;
  }
  .av-banner.info { background: rgba(59,130,246,0.06); color: #2563eb; }
  .av-banner.warn { background: rgba(234,179,8,0.06); color: #854d0e; }
  .av-banner.success { background: rgba(34,197,94,0.06); color: #16a34a; }
  .av-banner strong { display: block; font-size: 14px; margin-bottom: 2px; }
  .av-banner span { font-size: 13px; opacity: 0.85; }

  /* interviewer grid */
  .iv-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(270px,1fr)); gap: 16px; }
  .iv-section-hdr {
    display: flex; align-items: center; gap: 8px;
    font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px;
    color: var(--text-muted); padding: 8px 2px 4px; grid-column: 1 / -1;
  }
  .iv-section-dot {
    width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
  }
  .iv-section-dot.recommended { background: #22c55e; }
  .iv-section-dot.others { background: #94a3b8; }
  .iv-section-dot.current { background: #0ea5e9; }
  .match-badge {
    font-size: 10px; font-weight: 700; padding: 3px 8px;
    border-radius: 10px; white-space: nowrap; flex-shrink: 0;
  }
  .match-full { background: rgba(34,197,94,0.12); color: #16a34a; }
  .match-partial { background: rgba(234,179,8,0.12); color: #92400e; }
  .match-current { background: rgba(14,165,233,0.12); color: #0369a1; }
  .iv-card {
    border: 2px solid var(--border); border-radius: 12px; padding: 18px; transition: border-color 0.2s;
  }
  .iv-card-active { border-color: var(--primary-navy); background: rgba(45,55,72,0.02); }
  .iv-card-current { border-color: #bae6fd; background: #f0f9ff; }
  .iv-card-head { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 14px; }
  .iv-avatar {
    width: 42px; height: 42px; border-radius: 50%; background: var(--primary-navy);
    color: white; display: flex; align-items: center; justify-content: center;
    font-size: 17px; font-weight: 600; flex-shrink: 0;
  }
  .iv-name { font-size: 15px; font-weight: 600; margin-bottom: 4px; }
  .iv-tags { display: flex; gap: 6px; flex-wrap: wrap; }
  .tag {
    font-size: 11px; color: var(--text-muted); background: var(--accent-light,#f8f9fa);
    padding: 2px 7px; border-radius: 4px;
  }
  .slot-status { font-size: 13px; color: var(--text-muted); padding: 4px 0; }
  .slot-status.muted { font-style: italic; }

  .slot-times { display: flex; gap: 8px; flex-wrap: wrap; }
  .slot-btn {
    padding: 7px 14px; border: 1px solid var(--border); background: white;
    border-radius: 8px; font-size: 13px; cursor: pointer; transition: all 0.15s;
  }
  .slot-btn:hover { border-color: var(--primary-navy); background: rgba(45,55,72,0.04); }
  .slot-btn-active { background: var(--primary-navy); color: white; border-color: var(--primary-navy); }

  /* booking row */
  .book-row {
    display: flex; flex-direction: column; align-items: center; gap: 12px;
    padding: 24px; background: rgba(34,197,94,0.04);
    border: 2px solid rgba(34,197,94,0.2); border-radius: 14px;
  }
  .book-summary {
    display: flex; align-items: center; gap: 8px;
    font-size: 15px; color: var(--text-dark);
  }
  .tc-checkbox {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 14px 18px; background: rgba(255,255,255,0.7);
    border: 1px solid rgba(34,197,94,0.3); border-radius: 10px;
    cursor: pointer; font-size: 14px; color: var(--text-dark); max-width: 480px;
  }
  .tc-checkbox input[type="checkbox"] {
    margin-top: 2px; width: 17px; height: 17px; flex-shrink: 0;
    accent-color: var(--primary-navy); cursor: pointer;
  }
  .tc-checkbox strong { color: var(--primary-navy); }
  .btn:disabled { opacity: 0.45; cursor: not-allowed; }

  /* job description upload */
  .jd-upload-row {
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px;
  }
  .jd-upload-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 8px 16px; border: 2px dashed var(--border); border-radius: 8px;
    font-size: 13px; font-weight: 500; color: var(--primary-navy);
    background: rgba(45,55,72,0.03); cursor: pointer; transition: all 0.15s;
  }
  .jd-upload-btn:hover { border-color: var(--primary-navy); background: rgba(45,55,72,0.06); }
  .jd-file-pill {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 7px 12px; border-radius: 20px; font-size: 13px; font-weight: 500;
    border: 1px solid var(--border); background: var(--accent-light,#f8f9fa);
  }
  .jd-file-pill.uploading { color: #2563eb; border-color: #93c5fd; background: rgba(59,130,246,0.06); }
  .jd-file-pill.uploaded { color: #16a34a; border-color: #86efac; background: rgba(34,197,94,0.06); }
  .jd-remove-btn {
    background: none; border: none; cursor: pointer; padding: 2px;
    color: var(--text-muted); display: flex; align-items: center;
  }
  .jd-remove-btn:hover { color: #dc2626; }
  .jd-error { font-size: 12px; color: #dc2626; }
  .jd-divider {
    display: flex; align-items: center; gap: 10px; margin: 12px 0 10px;
    font-size: 12px; color: var(--text-muted);
  }
  .jd-divider::before, .jd-divider::after {
    content: ''; flex: 1; height: 1px; background: var(--border);
  }

  /* spinner */
  .spin-sm {
    width: 15px; height: 15px; border: 2px solid rgba(0,0,0,0.15);
    border-top-color: currentColor; border-radius: 50%;
    animation: spin 0.7s linear infinite; flex-shrink: 0; display: inline-block;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  @media (max-width: 768px) {
    .sch-grid { grid-template-columns: 1fr; }
    .prefs-cal-row { grid-template-columns: 1fr; }
    .radio-group { flex-direction: column; }
    .val-modal { padding: 20px; }
  }
`
